import { resolveNodeByUrlKey } from '@skill-trees/shared';
import { parseTreePath } from '../routing/routing';

export type DescriptionClickAction =
    | { type: 'copy_section_link'; headingId: string }
    | { type: 'navigate_to_node'; nodeId: string; hash?: string; pathname?: string; search?: string }
    | { type: 'none' };

interface ClickContext {
    currentPathname: string;
    currentOrigin: string;
    nodes: readonly { id: string }[];
}

/**
 * Determines what action to take when a user clicks inside the skill description body.
 * Pure function: no side effects, fully testable.
 */
export function resolveDescriptionClickAction(
    target: Element,
    container: Element,
    context: ClickContext,
): DescriptionClickAction {
    const sectionCopy = target.closest('.section-copy-link');
    if (sectionCopy instanceof HTMLButtonElement && container.contains(sectionCopy)) {
        const headingId = sectionCopy.getAttribute('data-heading-id');
        if (headingId) {
            return { type: 'copy_section_link', headingId };
        }
        return { type: 'none' };
    }

    const anchor = target.closest('a');
    if (!(anchor instanceof HTMLAnchorElement) || !container.contains(anchor)) {
        return { type: 'none' };
    }

    if (anchor.target === '_blank' || anchor.hasAttribute('download')) {
        return { type: 'none' };
    }

    const href = anchor.getAttribute('href');
    if (!href || href.startsWith('#')) {
        return { type: 'none' };
    }

    let url: URL;
    try {
        url = new URL(href, context.currentOrigin + context.currentPathname);
    } catch {
        return { type: 'none' };
    }

    const currentTreeId = parseTreePath(context.currentPathname).treeId;
    const parsedTarget = parseTreePath(url.pathname);
    if (!currentTreeId || parsedTarget.treeId !== currentTreeId || !parsedTarget.nodeSegment) {
        return { type: 'none' };
    }

    const node = resolveNodeByUrlKey(context.nodes, parsedTarget.nodeSegment);
    if (!node) {
        return { type: 'none' };
    }

    const action: DescriptionClickAction = { type: 'navigate_to_node', nodeId: node.id };
    if (url.hash) {
        action.hash = url.hash;
        action.pathname = url.pathname;
        action.search = url.search;
    }
    return action;
}
